import {
  BrianWordmark,
  DisplayClock,
  TriangleExclamationIcon,
} from "@scribenote/brian-design-system";
import { formatCountdown } from "@utils/format-date";
import { useNow } from "@utils/use-now";

import { useDisplayPairing } from "./useDisplayPairing";

const CountdownText = ({ expiresAt }: { expiresAt: number }) => {
  const now = useNow(1000);
  return (
    <p className="text-mono-time text-[1.1875rem] text-annotation">
      New code in{" "}
      {formatCountdown(Math.max(0, Math.ceil((expiresAt - now) / 1000)))}
    </p>
  );
};

export const DisplayPairingScreen = () => {
  const { state, cookieRefused } = useDisplayPairing();
  const showing = state.status === "showing" ? state : null;

  return (
    <div className="flex min-h-dvh flex-col gap-8 bg-wall px-8 pt-7 pb-10 font-body text-ink-body">
      <header className="flex items-center justify-between gap-6">
        <BrianWordmark mark={false} className="text-3xl" />
        <DisplayClock className="text-lg" />
      </header>

      <main className="m-auto flex w-full max-w-6xl flex-col items-center gap-6 rounded-paper bg-card px-8 py-16 text-center shadow-sheet">
        {showing ? (
          <>
            <p
              key={showing.userCode}
              data-testid="display-pairing-code"
              className="font-mono text-[clamp(3.5rem,10vw,8.5rem)] leading-none font-semibold tracking-[0.12em] whitespace-nowrap text-ink tabular-nums [animation:bds-slip-in_220ms_var(--ease-paper)_backwards] motion-reduce:animate-none"
            >
              {showing.userCode}
            </p>
            <p className="text-[1.5rem] text-ink">
              Enter this code in Clinic settings › Displays
            </p>
            <CountdownText expiresAt={showing.expiresAt} />
          </>
        ) : (
          <p className="py-10 text-[1.5rem] text-annotation" aria-live="polite">
            {state.status === "starting"
              ? "Getting a code."
              : "I can’t reach Brian. Trying again shortly."}
          </p>
        )}

        {cookieRefused && (
          <p className="flex items-center gap-2 text-[1.1875rem] text-ink-body">
            <TriangleExclamationIcon className="text-ink-stopped" />
            This browser didn’t keep the pairing. Allow cookies for this site,
            then enter the new code.
          </p>
        )}
      </main>

      <a
        href="/"
        className="self-center rounded-[2px] text-sm text-annotation underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        Back to sign in
      </a>
    </div>
  );
};
